import labels, { fileUploadErrors } from "../../../../../../data/labels";
import Loader from "../../../../../loader/Loader";
import Information from './components/information/Information';
import useInfo from '../../../../../../hooks/useInfo';
import FileInput from '../../../../../file-input/FileInput';
import FileListActions from '../../../../../file-list-actions/FileListActions';
import FileList from '../../../../../file-list/FileList';
import FileListActionsModal from '../../../../../file-list-actions-modal/FileListActionsModal';
import useDocumentEvents from '../../../../../../elements/hooks/useDocumentEvents';
import { useState, useCallback, useRef, useEffect, useMemo, ReactNode } from "react";
import { useChat } from "../../../../../../contexts/chat/ChatContext";
import { DocumentInfoModel, DocumentsResponse, DocumentUpdateRequest } from "../../../../../../models/ChatTypes";
import { TestIds } from "../../../../../../mocks/ids";
import { useErrorHandlerContext } from '../../../../../../contexts/error-handler/ErrorContext';
import { getExtension, readableFileSize } from '../../../../../../helpers/fileHelpers';
import { ErrorHandler } from '../../../../../../contexts/error-handler/ErrorHandler';
import { AxiosProgressEvent } from 'axios';
import { useAppInfo } from '../../../../../../contexts/app-info/AppInfoContext';
import { useAdaptive } from '../../../../../../contexts/adaptive/AdaptiveContext';
import { DEMO_TOUR_FILE_UPLOAD_KEY, DEMO_TOUR_SUCCESS_FILE_UPLOAD_KEY, TourFileUploadTargets } from '../../../../../../features/tours/hooks/useTourFileUploads';
import { isString } from '../../../../../../helpers/typeHelpers';
import { SelectStatic } from '../../../../../select/Select';
import { advancedAssistantKey, advancedAssistantRoute, tenderAssistantKey } from '../../../../../../constants/consts';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileUploadStatus, useFileListActions, useFileListState } from '../../../../../../hooks/useFileUpload';
import styles from "./Document.module.scss";
import { useFileDownloadToast } from '../../../../../../hooks/useFileDownloadToast';
import useLabels from '../../../../../../hooks/useLabels';

import { DocumentProcessingMode } from '../../../../../../models/types';
import DocumentEditModal from './components/edit/DocumentEditModal';
import useDebouncedEffect from '../../../../../../hooks/useDebouncedEffect';
import EventHandler from '../../../../../../services/EventHandler';


export const Documents = ({ chatId, objectId, showInfo = true, showDescriptionCta = true, reportUploadStateChange }: { chatId: string; objectId?: string; showInfo?: boolean; showDescriptionCta?: boolean; reportUploadStateChange?: (data?: DocumentInfoModel[]) => void; }) => {
    const isDemoTour = useMemo(() => [DEMO_TOUR_FILE_UPLOAD_KEY, DEMO_TOUR_SUCCESS_FILE_UPLOAD_KEY].includes(objectId || ''), [objectId]);

    const demoTour: DocumentInfoModel[] = useMemo(() => [
        {
            id: 'demo-tour',
            user: '...',
            type: 'pdf',
            format: 'pdf',
            size: 1345674,
            fileName: 'Uploaded document name',
            description: 'This document contains some important information.',

            modified: new Date().toISOString(),
            created: new Date().toISOString(),

            display: {
                name: 'Demo Tour File',
                description: 'This is a demo tour file.'
            },

            state: {
                status: objectId === DEMO_TOUR_SUCCESS_FILE_UPLOAD_KEY ? FileUploadStatus.Ready : FileUploadStatus.Processing,
            },
        }
    ], [objectId]);


    const navigate = useNavigate();
    const { hash } = useLocation();
    const { chatInfo } = useAdaptive();
    const { eventDictionary } = useDocumentEvents();
    const { getPersonaVersion } = useInfo();

    const { documents: globalDocuments } = useAppInfo();
    const { persistent: { getInput, mergeInput, clearFileQueue, getFileQueue }, uploadDocument, fetchDocuments } = useChat();
    const { updateDocumentMetadata, deleteDocuments, downloadDocuments, copyDocuments, persistent: { setInput } } = useChat();
    const { errorId, registerError, removeError } = useErrorHandlerContext();

    const initialLoadRef = useRef(false);
    const [data, setData] = useState<DocumentInfoModel[]>([]);

    const [isLoading, setIsLoading] = useState(false);
    const [activeModal, setActiveModal] = useState<string | undefined>(undefined);
    const [editState, setEditState] = useState<Pick<DocumentInfoModel, 'id' | 'display'>>({ id: '', display: { name: '', description: '' } });

    const documents = useMemo(() => getPersonaVersion(chatInfo.persona, chatInfo.version, chatInfo.build).revision?.documents || globalDocuments, [getPersonaVersion, globalDocuments, chatInfo]);


    const fileUploadProps = useFileListState({ files: data.map(x => ({ ...x, id: x.id })), events: eventDictionary });
    const renderSelectedNames = useMemo(() => fileUploadProps.renderSelectedNames({ ...fileUploadErrors }), [fileUploadProps]);

    useEffect(() => reportUploadStateChange?.(data as any), [data, reportUploadStateChange]);

    const externalFileEvent = useMemo(() => new EventHandler<File[]>(), []);
    const digestFileUploadQueue = useCallback(() => {
        const queuedFiles = getFileQueue();
        if (!queuedFiles.length) {
            return;
        }

        externalFileEvent.invoke(queuedFiles);
        clearFileQueue();
    }, [getFileQueue, clearFileQueue, externalFileEvent]);
    useDebouncedEffect(digestFileUploadQueue, 200);

    const persistentInput = useCallback(() => getInput(chatInfo.persona!, { documents: [] }), [getInput, chatInfo.persona]);
    const setDocumentIds = useCallback((documents: string[]) => mergeInput(chatInfo.persona!, { documents: [...(persistentInput().documents ?? []), ...documents] }), [mergeInput, persistentInput, chatInfo.persona]);
    const removeDocumentIds = useCallback((documents: string[]) => mergeInput(chatInfo.persona!, { documents: (persistentInput().documents ?? []).filter(doc => !documents.includes(doc)) }), [mergeInput, persistentInput, chatInfo.persona]);


    const loadDocuments = useCallback(async () => {
        removeError(errorId);
        setIsLoading(true);

        try {
            const documentIds = persistentInput().documents ?? [];
            if (!documentIds.length && !chatId)
                return;

            initialLoadRef.current = true;

            const response = await fetchDocuments({ fileIds: chatId ? [] : documentIds, sessionId: chatId });

            if (response.errors && Object.keys(response.errors).length > 0) {
                const errorMessages = Object.entries(response.errors).map(([_, errorMessage]) => `${errorMessage}`).join(", ");
                registerError({ [errorId]: { type: 'notification', headline: labels.fetchErrorHeadline, description: errorMessages } });
            }

            if (response.documents) {
                const rows = Object.values(response.documents);
                setData(rows);
            }
        }
        catch (error: any) {
            registerError({ [errorId]: { type: 'notification', headline: labels.fetchErrorHeadline, description: labels.fetchErrorMessage, details: error } });
        }
        finally {
            setIsLoading(false);
        }
    }, [removeError, errorId, persistentInput, chatId, fetchDocuments, registerError]);

    // Init documents load
    useEffect(() => { !initialLoadRef.current && loadDocuments(); }, [loadDocuments]);


    const handleFileUpload = useCallback(async (file: File, setUploadProgress: (progress: AxiosProgressEvent) => void) => {
        // Check if the file size is valid
        const { name: fileName } = getExtension(file?.name) || { extension: 'unknown', name: 'undefined' };

        const props: DocumentInfoModel = await uploadDocument({ file, sessionId: chatId, onUploadProgress: setUploadProgress, processingMode: documents?.processing_mode });

        const { id, size, format, state } = props;
        if (!id) {
            console.error('Document ID is missing');
            return;
        }

        // Handle the case of new chat where the session ID is not yet available
        if (!chatId) setDocumentIds([id]);
        const uploadStatus = state?.status;
        if (!uploadStatus) {
            console.error('Upload status is missing');
            return;
        };

        setData(prev => [...prev, { ...props, fileName, description: '', documentId: id, size: size, extension: format, status: uploadStatus }]);
    }, [uploadDocument, chatId, setDocumentIds, documents?.processing_mode]);

    const fileDownloadToast = useFileDownloadToast();
    const handleDownload = useCallback(async () => {
        fileUploadProps.handleErrorRemoval();
        fileUploadProps.handleLoadingIdsChange();

        try {
            const fileDownloadPromise = downloadDocuments({ fileIds: fileUploadProps.selectedIds });
            fileDownloadToast(fileDownloadPromise);

            const success = await fileDownloadPromise;
            if (!success) fileUploadProps.handleErrorRegistering(labels.downloadErrorHeadline, labels.downloadErrorMessage);
            fileUploadProps.onDeselectAll();
        }
        catch (error) {
            fileUploadProps.handleErrorRegistering(labels.downloadErrorHeadline, labels.downloadErrorMessage, error);
        }
        finally {
            fileUploadProps.handleLoadingIdsChange([]);
        }
    }, [downloadDocuments, fileUploadProps, fileDownloadToast]);

    const handleDelete = useCallback(async () => {
        fileUploadProps.handleErrorRemoval();
        fileUploadProps.handleLoadingIdsChange();

        try {
            const { delete_document_ids, errors } = await deleteDocuments({ fileIds: fileUploadProps.selectedIds, sessionId: chatId });
            const hasDeletedDocs = delete_document_ids?.length > 0;

            if (errors && Object.keys(errors).length) {
                const errorMessages = Object.values(errors).join(", ");
                fileUploadProps.handleErrorRegistering(fileUploadErrors.deleteError.headline, `${fileUploadErrors.deleteError.message} ${errorMessages}`, errors);
            }

            if (hasDeletedDocs) {
                setData(prev => {
                    const remainingDocuments = prev.filter(row => !delete_document_ids.includes(row.id));
                    return remainingDocuments;
                });
            }

            if (hasDeletedDocs && !chatId) {
                removeDocumentIds(delete_document_ids);
                if (!isString(chatInfo.persona)) return;
            }

            fileUploadProps.onDeselectAll();
            setActiveModal(undefined);
        }
        catch (error) {
            fileUploadProps.handleErrorRegistering(labels.deleteErrorHeadline, labels.deleteErrorMessage, error);
        }
        finally {
            fileUploadProps.handleLoadingIdsChange([]);
        }
    }, [chatId, chatInfo.persona, deleteDocuments, fileUploadProps, removeDocumentIds]);

    const handleEdit = useCallback(async () => {
        fileUploadProps.handleErrorRemoval();
        fileUploadProps.handleLoadingIdsChange();
        const metadata: DocumentUpdateRequest = { display: { name: editState?.display.name, description: editState?.display.description } };

        try {
            const response = await updateDocumentMetadata({ fileId: editState?.id, metadata });
            if (!response) throw new Error('No response from updateDocumentMetadata');

            setData(prev => {
                const targetIndex = prev.findIndex((row => row.id === editState?.id));
                if (targetIndex === -1) {
                    console.error('Document not found in the list');
                    return prev;
                };

                prev.splice(targetIndex, 1, { ...prev[targetIndex], ...metadata, display: { ...metadata?.display, name: metadata?.display?.name || '-' } });

                return [...prev];
            });
            fileUploadProps.onDeselectAll();
            setActiveModal(undefined);
        }
        catch (error) {
            fileUploadProps.handleErrorRegistering(labels.updateErrorHeadline, labels.updateErrorMessage, error);
        }
        finally {
            fileUploadProps.handleLoadingIdsChange([]);
        }
    }, [editState?.display.description, editState?.display.name, editState?.id, fileUploadProps, updateDocumentMetadata]);

    const handleCopy = useCallback(async (selectedAssistant: string, hidePanelAfterCopy?: boolean) => {
        fileUploadProps.handleErrorRemoval();
        setIsLoading(true);

        const result: DocumentsResponse = await copyDocuments({ document_ids: fileUploadProps.selectedIds });

        try {
            if (result.errors && Object.keys(result.errors).length > 0) {
                const errorMessages = Object.values(result.errors).join(", ");
                fileUploadProps.handleErrorRegistering(fileUploadErrors.copyError.headline, errorMessages);
            }
            else {
                // Extract document IDs from result.documents
                const documents = Object.values(result.documents).map((doc) => doc.id);

                // Refresh documents list with new documents
                await loadDocuments(); // Prop of 'documents' used to be passed in?

                // Update document IDs in persistent state
                const assistantKey = selectedAssistant === advancedAssistantKey ? advancedAssistantRoute : selectedAssistant;
                setInput(assistantKey, { documents });

                const personaKey = getPersonaVersion(selectedAssistant).persona?.route;

                // Navigate to the chat route if not already there
                navigate({ pathname: '/' + personaKey, hash: hidePanelAfterCopy ? '' : hash });
            }
        }
        catch (error: any) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            fileUploadProps.handleErrorRegistering(labels.copyErrorHeadline, errorMessage, error);
        }
        finally {
            setIsLoading(false);
        }
    }, [copyDocuments, fileUploadProps, getPersonaVersion, hash, loadDocuments, navigate, setInput]);


    const actions = useFileListActions({
        copy: {
            onClick: () => setActiveModal('copy'),
            isDisabled: !fileUploadProps.selectedIds.length,
            isHidden: !chatId,
        },
        remove: {
            onClick: () => setActiveModal('remove'),
            isDisabled: !fileUploadProps.selectedIds.length,
        },
        download: {
            onClick: handleDownload,
            isDisabled: !fileUploadProps.selectedIds.length,
        },
        edit: {
            onClick: () => {
                const file = fileUploadProps.files.find(file => fileUploadProps.selectedIds.includes(file.id));

                if (!file) return console.error('File not found');
                setEditState(file);
                setActiveModal('edit');
            },
            isDisabled: fileUploadProps.selectedIds.length !== 1,
        },
        refresh: {
            onClick: () => loadDocuments(),
        }
    });


    return (
        <div className={styles.wrapper} data-testid={TestIds.documentView}>
            <FileInput
                fileList={data}
                maxUploadNumber={documents?.max}
                maxFileUploadSize={documents?.length}
                allowedFileFormats={documents?.formats}
                onFileUpload={handleFileUpload}
                externalFileUploadEvent={externalFileEvent}
            />

            <ErrorHandler.Notifications className={styles.error} id={errorId} />

            {isLoading
                ? <Loader />
                : <>
                    {isDemoTour ? true : !!data.length && <FileListActions
                        {...fileUploadProps}
                        actions={actions}
                    />}

                    <FileList
                        chipId={TourFileUploadTargets.statusIndicator}
                        {...fileUploadProps}
                        labels={fileUploadErrors}
                        files={isDemoTour ? demoTour : data}
                        onEdit={({ fileId: documentId, name: fileName, description }) => {
                            setEditState({ id: documentId, display: { name: fileName, description } });
                            setActiveModal('edit');
                        }}
                        showDescriptionCta={showDescriptionCta}
                        showTableSearchConfig={documents?.processing_mode === DocumentProcessingMode.processNonTabular}
                    />


                    <FileListActionsModal
                        isOpen={activeModal === 'remove'}
                        modal={{
                            headline: labels.deleteDocumentModalTitle
                        }}
                        close={{
                            label: labels.cancel,
                            onClick: () => setActiveModal(undefined)
                        }}
                        submit={{
                            label: labels.delete,
                            onClick: () => handleDelete()
                        }}
                        errorId={errorId}
                    >
                        <div>
                            {labels.deleteDocumentConfirmationText} {renderSelectedNames}
                        </div>
                    </FileListActionsModal>

                    <DocumentEditModal
                        isOpen={activeModal === 'edit'}
                        onClose={() => setActiveModal(undefined)}
                        onSubmit={handleEdit}
                        editState={editState}
                        setEditState={setEditState}
                        showTableSearchConfig={documents?.processing_mode === DocumentProcessingMode.processNonTabular}
                        document={data.find(f => f.id === editState.id)}
                    />
                    <CopyModal
                        onCopy={handleCopy}
                        errorId={fileUploadProps.errorId}
                        activeModal={activeModal}
                        setActiveModal={setActiveModal}
                        renderSelectedNames={renderSelectedNames}
                        selectedItems={fileUploadProps.selectedIds.length ? data.filter(file => fileUploadProps.selectedIds.includes(file.id)) : undefined}
                    />

                    {showInfo && <Information />}
                </>
            }
        </div>
    );
};


const CopyModal = ({ errorId, activeModal, selectedItems, renderSelectedNames, onCopy, setActiveModal, }: { errorId: string, selectedItems?: DocumentInfoModel[], activeModal?: string, renderSelectedNames: ReactNode; onCopy: (selectedAssistant: string, minimizeSidePanel?: boolean) => void, setActiveModal: (state?: string) => void, }) => {
    const labels = useLabels();
    const { chatInfo } = useAdaptive();
    const { allowedPersonaOptions, getPersonaVersion } = useInfo();

    const [selectedAssistant, setSelectedAssistant] = useState<string | undefined>(chatInfo.persona);
    const { revision: { documents } = { documents: undefined } } = getPersonaVersion(selectedAssistant || '-') ?? {};
    const { formats, length: maxSize, max } = documents || {};

    const readableAssistantList = useCallback((list: Partial<Pick<DocumentInfoModel, 'display'>>[]) => list.map?.(({ display }) => display?.name).join(', '), []);

    const inValidMessage = useMemo(() => {
        if (max && selectedItems && selectedItems?.length > max) return labels.copyErrorValidateMaxSize(max);

        const exceedsSize = maxSize ? selectedItems?.filter(({ size }) => size > maxSize) : [];
        if (!!exceedsSize?.length) return labels.copyErrorValidateMaxLimit(readableFileSize(maxSize), readableAssistantList(exceedsSize));

        const unsupportedFormats = formats && selectedItems?.filter(({ format, }) => !formats.includes(format));
        if (!!unsupportedFormats?.length) return labels.copyErrorValidateExtension(readableAssistantList(unsupportedFormats));
    }, [labels, formats, max, maxSize, readableAssistantList, selectedItems]);


    const allowedOptions = useMemo(() => allowedPersonaOptions
        .filter(a => a.versions
            .some(v => v.revisions
                .some(r => r.documents?.enabled)
            )
        ).map(({ title: label, key: value, versions }) => ({
            value,
            label,
            versions: versions.map(({ version, revisions }) => ({
                version,
                revisions: revisions.map(({ build, label }) => ({
                    build, label
                }))
            }))
        })), [allowedPersonaOptions]);

    const renderSelectAssistant = useMemo(() => {
        return <div className={styles['select-container']}>
            {allowedOptions.length > 1 && <SelectStatic
                label={labels.copySelectChatAssistant}
                value={selectedAssistant}
                options={allowedOptions}
                onChange={setSelectedAssistant}
            />}
        </div>;
    }, [allowedOptions, labels.copySelectChatAssistant, selectedAssistant, setSelectedAssistant]);

    const renderInValidMessage = useMemo(() => {
        if (!inValidMessage) return null;
        return <div className={styles['invalid-message']}>
            {inValidMessage}
        </div>;
    }, [inValidMessage]);

    const confirmCopy = useCallback(async () => {
        if (!isString(selectedAssistant)) return;
        const minimizeSidePanel = [tenderAssistantKey].includes(selectedAssistant);
        await onCopy(selectedAssistant, minimizeSidePanel);
        setActiveModal(undefined);
    }, [onCopy, selectedAssistant, setActiveModal]);

    return <FileListActionsModal
        isOpen={activeModal === 'copy'}
        modal={{
            headline: labels.copyToNewChatHeadline
        }}
        close={{
            label: labels.copyToNewChatCancelCta,
            onClick: () => setActiveModal(undefined)
        }}
        submit={{
            label: labels.copyToNewChatCta,
            onClick: confirmCopy,
            isDisabled: !!inValidMessage
        }}
        errorId={errorId}
    >
        <>
            {labels.copyToNewChatDescription}{renderSelectedNames}
            <br />
            <div className={styles.content}>
                {renderSelectAssistant}
                {renderInValidMessage}
            </div>
        </>
    </FileListActionsModal>;
};
